import { MilestoneMap1, MilestoneMapInterval } from '../types';
import {
  standardizeStartDate,
  getIntervalEnd,
  standardizeToQuarterStart,
  standardizeToQuarterEnd,
} from './intervalUtils';

/**
 * Generates time intervals for a milestone map.
 * @param map - The milestone map
 * @param intervalOverride - Optional interval to use instead of map.interval (for zoom functionality)
 * @returns Array of interval objects with start and end dates
 */
export function getIntervalsForMilestoneMap(
  map: MilestoneMap1,
  intervalOverride?: MilestoneMapInterval,
): Array<{ start: Date; end: Date }> {
  const startDate = new Date(map.startDate);
  const endDate = new Date(map.endDate);
  const intervals = [];
  const intervalType = intervalOverride || map.interval;

  switch (intervalType) {
    case 'hours': {
      let current = standardizeStartDate(startDate, 'hours');
      while (current <= endDate) {
        const hourStart = new Date(current);
        const hourEnd = getIntervalEnd(hourStart, 'hours');
        intervals.push({ start: hourStart, end: hourEnd });
        current.setHours(current.getHours() + 1);
      }
      break;
    }
    case 'days': {
      let current = standardizeStartDate(startDate, 'days');
      while (current <= endDate) {
        const dayStart = new Date(current);
        const dayEnd = getIntervalEnd(dayStart, 'days');
        intervals.push({ start: dayStart, end: dayEnd });
        current.setDate(current.getDate() + 1);
      }
      break;
    }
    case 'weeks': {
      // TODO: Review/Revise in the future to allow flexibility
      // for timing week start/end with sprint start/end dates
      // (e.g., sprint starts on Wednesday, ends on Tuesday).
      // For now, we're standardizing the start date to the beginning
      // of the week, like the other intervals.
      let current = standardizeStartDate(startDate, 'weeks');
      while (current <= endDate) {
        const weekStart = new Date(current);
        const weekEnd = getIntervalEnd(weekStart, 'weeks');
        intervals.push({ start: weekStart, end: weekEnd });
        current.setDate(current.getDate() + 7);
      }
      break;
    }
    case 'months': {
      let current = standardizeStartDate(startDate, 'months');
      while (current <= endDate) {
        const monthStart = new Date(current);
        const monthEnd = getIntervalEnd(monthStart, 'months');
        intervals.push({ start: monthStart, end: monthEnd });
        current.setMonth(current.getMonth() + 1);
        current.setDate(1);
      }
      break;
    }
    case 'quarters': {
      // First standardize the start date to the beginning of its quarter
      let current = standardizeToQuarterStart(startDate);
      while (current <= endDate) {
        const quarterStart = new Date(current);
        // For the end date, we need to look at the quarter start date
        // to determine the correct quarter end
        const quarterEnd = standardizeToQuarterEnd(quarterStart);
        intervals.push({ start: quarterStart, end: quarterEnd });
        // Move to start of next quarter
        current.setMonth(current.getMonth() + 3);
        current.setDate(1);
      }
      break;
    }
    case 'years': {
      let current = standardizeStartDate(startDate, 'years');
      while (current <= endDate) {
        const yearStart = new Date(current);
        const yearEnd = getIntervalEnd(yearStart, 'years');
        intervals.push({ start: yearStart, end: yearEnd });
        current.setFullYear(current.getFullYear() + 1);
        current.setMonth(0, 1);
      }
      break;
    }
    default:
      throw new Error(
        `Interval '${intervalType}' is not supported. Only 'hours', 'days', 'weeks', 'months', 'quarters', and 'years' are allowed.`,
      );
  }

  return intervals;
}
